import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LineChart } from "lucide-react";
import { buildGscSectionHref } from "@/lib/gsc-section-href";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const GSC_SIDEBAR_ITEMS = [
  { path: "/gsc", label: "Overview" },
  { path: "/gsc/detail", label: "Detail" },
  { path: "/gsc/compare", label: "Compare" },
] as const;

interface GscSidebarNavProps {
  onClose: () => void;
}

export const GscSidebarNav = ({ onClose }: GscSidebarNavProps) => {
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const isGscActive = pathname.startsWith("/gsc");

  const hrefByPath = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of GSC_SIDEBAR_ITEMS) {
      map.set(item.path, buildGscSectionHref(item.path, search));
    }
    return map;
  }, [search]);

  const handleNavigate = () => {
    onClose();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="GSC"
          aria-label="GSC analytics"
          aria-expanded={open}
          className={cn(
            "relative flex w-full items-center justify-center py-4 transition-colors duration-200",
            isGscActive
              ? "bg-sidebar-accent text-sidebar-foreground"
              : "text-sidebar-foreground/55 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground",
          )}
        >
          {isGscActive && (
            <div
              className="absolute top-0 bottom-0 left-0 w-1 rounded-r-full bg-[var(--chart-2)]"
              aria-hidden
            />
          )}
          <LineChart
            className={cn(
              "size-6",
              isGscActive ? "stroke-[2.5px]" : "stroke-[2px]",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        className="dark w-44 border-border bg-sidebar p-2 text-sidebar-foreground shadow-none"
      >
        <p className="px-2 py-1 text-xs font-medium tracking-wide text-sidebar-foreground/60 uppercase">
          GSC
        </p>
        <div className="flex flex-col gap-0.5">
          {GSC_SIDEBAR_ITEMS.map((item) => {
            const isActive =
              item.path === "/gsc"
                ? pathname === item.path
                : pathname.startsWith(item.path);

            return (
              <Link
                key={item.path}
                to={hrefByPath.get(item.path) ?? item.path}
                onClick={handleNavigate}
                className={cn(
                  "rounded-md px-2 py-1.5 text-sm transition-colors",
                  isActive
                    ? "bg-sidebar-accent text-sidebar-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
                )}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
